/**
 * Sale Collection Template - Cart Management
 * Handles add to cart functionality, quantity controls, and sticky checkout bar
 */

interface CartResponse {
  item_count: number;
  items: Array<{
    id: number;
    quantity: number;
    variant_id: number;
    price: number;
    original_price?: number;
    final_price: number;
    properties?: Record<string, string>;
    sku?: string;
    title?: string;
    product_title?: string;
    variant_title?: string;
    image?: string;
    url?: string;
  }>;
  total_price: number;
}

interface AddToCartResponse {
  id: number;
  quantity: number;
  variant_id: number;
  product_id: number;
}

interface ProductData {
  title: string;
  saleTitle?: string;
  description: string;
  information?: string;
  outline?: string;
  images: Array<{
    src: string;
    alt: string;
    width: number;
    height: number;
  }>;
}

class ProductModal {
  private modal: HTMLElement | null = null;
  private modalTitle: HTMLElement | null = null;
  private modalDescription: HTMLElement | null = null;
  private modalInformation: HTMLElement | null = null;
  private modalOutline: HTMLElement | null = null;
  private modalPanel: HTMLElement | null = null;
  private modalHeader: HTMLElement | null = null;
  private galleryTrack: HTMLElement | null = null;
  private galleryDots: HTMLElement | null = null;
  private prevButton: HTMLButtonElement | null = null;
  private nextButton: HTMLButtonElement | null = null;
  private closeButton: HTMLButtonElement | null = null;
  private backdrop: HTMLElement | null = null;
  private currentImageIndex: number = 0;
  private totalImages: number = 0;
  private focusableElements: HTMLElement[] = [];
  private lastFocusedElement: HTMLElement | null = null;
  private scrollLockY: number = 0;
  private boundPanelScroll: (() => void) | null = null;

  constructor() {
    this.boundPanelScroll = this.handlePanelScroll.bind(this);
    this.init();
  }

  private decodeHtmlEntities(str: string): string {
    const textarea = document.createElement('textarea');
    textarea.innerHTML = str;
    return textarea.value;
  }

  private init(): void {
    this.createModal();
    this.attachEventListeners();
  }

  private createModal(): void {
    if (document.querySelector('.sale-product-modal')) {
      this.modal = document.querySelector('.sale-product-modal');
    } else {
      const modalHTML = `
        <div class="sale-product-modal" role="dialog" aria-modal="true" aria-labelledby="saleModalTitle" aria-hidden="true">
          <div class="sale-product-modal__backdrop"></div>
          <div class="sale-product-modal__panel">
            <div class="sale-product-modal__header">
              <h2 id="saleModalTitle" class="sale-product-modal__title"></h2>
              <button type="button" class="sale-product-modal__close" aria-label="閉じる">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </button>
            </div>
            
            <div class="sale-product-modal__gallery">
              <div class="sale-product-modal__gallery-container">
                <div class="sale-product-modal__gallery-track"></div>
              </div>
              <button type="button" class="sale-product-modal__gallery-prev" aria-label="前の画像">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M15 18L9 12L15 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </button>
              <button type="button" class="sale-product-modal__gallery-next" aria-label="次の画像">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 18L15 12L9 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </button>
              <div class="sale-product-modal__gallery-dots"></div>
            </div>
            
            <div class="sale-product-modal__content">
              <div class="sale-product-modal__description"></div>
              <div class="sale-product-modal__information"></div>
              <div class="sale-product-modal__outline"></div>
            </div>
          </div>
        </div>
      `;
      
      document.body.insertAdjacentHTML('beforeend', modalHTML);
      this.modal = document.querySelector('.sale-product-modal');
    }

    this.modalPanel = this.modal?.querySelector('.sale-product-modal__panel') || null;
    this.modalHeader = this.modal?.querySelector('.sale-product-modal__header') || null;
    this.modalTitle = this.modal?.querySelector('.sale-product-modal__title') || null;
    this.modalDescription = this.modal?.querySelector('.sale-product-modal__description') || null;
    this.modalInformation = this.modal?.querySelector('.sale-product-modal__information') || null;
    this.modalOutline = this.modal?.querySelector('.sale-product-modal__outline') || null;
    this.galleryTrack = this.modal?.querySelector('.sale-product-modal__gallery-track') || null;
    this.galleryDots = this.modal?.querySelector('.sale-product-modal__gallery-dots') || null;
    this.prevButton = this.modal?.querySelector('.sale-product-modal__gallery-prev') || null;
    this.nextButton = this.modal?.querySelector('.sale-product-modal__gallery-next') || null;
    this.closeButton = this.modal?.querySelector('.sale-product-modal__close') || null;
    this.backdrop = this.modal?.querySelector('.sale-product-modal__backdrop') || null;
  }

  private attachEventListeners(): void {
    document.addEventListener('click', (e: Event) => {
      const target = e.target as HTMLElement;
      const link = target.closest('.sale-product-detail-link') as HTMLElement;
      
      if (link) {
        e.preventDefault();
        const productHandle = link.dataset.productHandle;
        if (productHandle) {
          this.openModal(productHandle);
        }
      }
    });

    this.closeButton?.addEventListener('click', () => this.closeModal());

    this.backdrop?.addEventListener('click', () => this.closeModal());

    document.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key === 'Escape' && this.modal?.getAttribute('aria-hidden') === 'false') {
        this.closeModal();
      }
    });

    this.prevButton?.addEventListener('click', () => this.navigateGallery(-1));
    this.nextButton?.addEventListener('click', () => this.navigateGallery(1));

    document.addEventListener('keydown', (e: KeyboardEvent) => {
      if (this.modal?.getAttribute('aria-hidden') === 'false') {
        if (e.key === 'ArrowLeft') {
          this.navigateGallery(-1);
        } else if (e.key === 'ArrowRight') {
          this.navigateGallery(1);
        }
      }
    });
  }

  private openModal(productHandle: string): void {
    const productData = this.getProductData(productHandle);
    if (!productData) {
      console.error('Product data not found for handle:', productHandle);
      return;
    }

    this.lastFocusedElement = document.activeElement as HTMLElement;

    if (this.modalTitle) {
      this.modalTitle.textContent = productData.saleTitle || productData.title;
    }

    if (this.modalDescription) {
      this.modalDescription.innerHTML = productData.description || '';
    }

    if (this.modalInformation) {
      const infoHtml = productData.information ? this.decodeHtmlEntities(productData.information) : '';
      this.modalInformation.innerHTML = infoHtml;
      this.modalInformation.style.display = infoHtml ? 'block' : 'none';
    }

    if (this.modalOutline) {
      const outlineHtml = productData.outline ? this.decodeHtmlEntities(productData.outline) : '';
      this.modalOutline.innerHTML = outlineHtml;
      this.modalOutline.style.display = outlineHtml ? 'block' : 'none';
    }

    this.buildGallery(productData.images);

    this.scrollLockY = window.scrollY || window.pageYOffset;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this.scrollLockY}px`;
    document.body.style.width = '100%';
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }
    document.body.classList.add('sale-modal-open');

    this.modal?.setAttribute('aria-hidden', 'false');

    if (this.boundPanelScroll) {
      this.modalPanel?.addEventListener('scroll', this.boundPanelScroll);
    }

    this.setupFocusTrap();

    setTimeout(() => {
      this.closeButton?.focus();
    }, 100);
  }

  private handlePanelScroll(): void {
    if (!this.modalPanel || !this.modalHeader) return;
    
    if (this.modalPanel.scrollTop > 0) {
      this.modalHeader.classList.add('scrolled');
    } else {
      this.modalHeader.classList.remove('scrolled');
    }
  }

  private closeModal(): void {
    this.modal?.setAttribute('aria-hidden', 'true');

    if (this.boundPanelScroll) {
      this.modalPanel?.removeEventListener('scroll', this.boundPanelScroll);
    }

    const handleTransitionEnd = (e: TransitionEvent) => {
      if (e.propertyName === 'transform' && e.target === this.modalPanel) {
        document.body.classList.remove('sale-modal-open');
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.paddingRight = '';
        window.scrollTo(0, this.scrollLockY);

        this.modalPanel?.removeEventListener('transitionend', handleTransitionEnd);
      }
    };

    this.modalPanel?.addEventListener('transitionend', handleTransitionEnd);

    setTimeout(() => {
      if (document.body.classList.contains('sale-modal-open')) {
        document.body.classList.remove('sale-modal-open');
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.paddingRight = '';
        window.scrollTo(0, this.scrollLockY);
      }
    }, 350);

    if (this.lastFocusedElement) {
      this.lastFocusedElement.focus();
    }

    this.currentImageIndex = 0;
  }

  private getProductData(productHandle: string): ProductData | null {
    const scriptElement = document.querySelector(`script[data-product-data="${productHandle}"]`);
    if (!scriptElement) return null;

    try {
      return JSON.parse(scriptElement.textContent || '{}');
    } catch (error) {
      console.error('Error parsing product data:', error);
      return null;
    }
  }

  private buildGallery(images: ProductData['images']): void {
    if (!this.galleryTrack || !this.galleryDots) return;

    this.totalImages = images.length;
    this.currentImageIndex = 0;

    this.galleryTrack.innerHTML = '';
    this.galleryDots.innerHTML = '';

    const track = this.galleryTrack;
    const dots = this.galleryDots;

    images.forEach((image, index) => {
      const slide = document.createElement('div');
      slide.className = 'sale-product-modal__gallery-slide';
      
      const img = document.createElement('img');
      img.src = image.src;
      img.alt = image.alt;
      img.loading = 'lazy';
      
      slide.appendChild(img);
      track.appendChild(slide);

      const dot = document.createElement('button');
      dot.className = `sale-product-modal__gallery-dot${index === 0 ? ' active' : ''}`;
      dot.setAttribute('aria-label', `画像 ${index + 1} を表示`);
      dot.addEventListener('click', () => this.goToSlide(index));
      dots.appendChild(dot);
    });

    this.updateNavigationButtons();
  }

  private navigateGallery(direction: number): void {
    const newIndex = this.currentImageIndex + direction;
    
    if (newIndex >= 0 && newIndex < this.totalImages) {
      this.goToSlide(newIndex);
    }
  }

  private goToSlide(index: number): void {
    this.currentImageIndex = index;

    if (this.galleryTrack) {
      this.galleryTrack.style.transform = `translateX(-${index * 100}%)`;
    }

    const dots = this.galleryDots?.querySelectorAll('.sale-product-modal__gallery-dot');
    dots?.forEach((dot, i) => {
      if (i === index) {
        dot.classList.add('active');
      } else {
        dot.classList.remove('active');
      }
    });

    this.updateNavigationButtons();
  }

  private updateNavigationButtons(): void {
    if (this.prevButton) {
      this.prevButton.disabled = this.currentImageIndex === 0;
    }

    if (this.nextButton) {
      this.nextButton.disabled = this.currentImageIndex === this.totalImages - 1;
    }

    if (this.totalImages <= 1) {
      if (this.prevButton) this.prevButton.style.display = 'none';
      if (this.nextButton) this.nextButton.style.display = 'none';
      if (this.galleryDots) this.galleryDots.style.display = 'none';
    } else {
      if (this.prevButton) this.prevButton.style.display = '';
      if (this.nextButton) this.nextButton.style.display = '';
      if (this.galleryDots) this.galleryDots.style.display = '';
    }
  }

  private setupFocusTrap(): void {
    if (!this.modal) return;

    const focusableSelectors = [
      'button:not([disabled])',
      'a[href]',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])'
    ];

    this.focusableElements = Array.from(
      this.modal.querySelectorAll(focusableSelectors.join(','))
    ) as HTMLElement[];

    this.modal.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;

      const firstElement = this.focusableElements[0];
      const lastElement = this.focusableElements[this.focusableElements.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement?.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement?.focus();
        }
      }
    });
  }
}

class SaleCart {
  private stickyCheckout: HTMLElement | null = null;
  private cartDrawer: HTMLElement | null = null;
  private cartDrawerPanel: HTMLElement | null = null;
  private cartDrawerBackdrop: HTMLElement | null = null;
  private cartDrawerItems: HTMLElement | null = null;
  private cartDrawerEmpty: HTMLElement | null = null;
  private selectedTags: Set<string> = new Set();
  private selectedT1Tags: Set<string> = new Set();
  private selectedT2Tags: Set<string> = new Set();
  private t1ToT2Map: Map<string, Set<string>> = new Map();
  private freeShipAchieved: boolean = false;
  private isUpdatingCart: boolean = false;
  private scrollLockY: number = 0;
  private hasOpenedDrawer: boolean = false;
  private readonly FREE_SHIPPING_THRESHOLD = 550000; // ¥5,500 in cents
  private readonly DISPLAY_THRESHOLD = 300000; // ¥3,000 in cents

  constructor() {
    this.init();
  }

  private init(): void {
    this.stickyCheckout = document.querySelector('.sale-sticky-checkout');
    this.cartDrawer = document.querySelector('.sale-cart-drawer');
    this.cartDrawerPanel = this.cartDrawer?.querySelector('.sale-cart-drawer__panel') || null;
    this.cartDrawerBackdrop = this.cartDrawer?.querySelector('.sale-cart-drawer__backdrop') || null;
    this.cartDrawerItems = this.cartDrawer?.querySelector('.sale-cart-drawer__items') || null;
    this.cartDrawerEmpty = this.cartDrawer?.querySelector('.sale-cart-drawer__empty') || null;
    
    this.initializeTagFilters();
    this.updateStickyCheckout();
    
    this.initializeQuantityButtonStates();
    this.attachCartDrawerListeners();

    document.addEventListener('click', (e: Event) => {
      const target = e.target as HTMLElement;
      
      const quantityBtn = target.closest('.sale-quantity__btn') as HTMLElement;
      if (quantityBtn) {
        this.handleQuantityChange(quantityBtn);
      }

      const addToCartBtn = target.closest('.sale-add-to-cart') as HTMLElement;
      if (addToCartBtn) {
        this.handleAddToCart(addToCartBtn);
      }

      const variantBtn = target.closest('.sale-variant-option') as HTMLElement;
      if (variantBtn) {
        this.handleVariantChange(variantBtn);
      }

      const tagFilterBtn = target.closest('.sale-tag-filter') as HTMLElement;
      if (tagFilterBtn) {
        this.handleTagFilter(tagFilterBtn);
      }

      const cartButton = target.closest('.sale-sticky-checkout__cart-button') as HTMLElement;
      if (cartButton) {
        this.openCartDrawer();
      }

      const drawerQuantityBtn = target.closest('.sale-cart-item__quantity-btn') as HTMLElement;
      if (drawerQuantityBtn) {
        this.handleDrawerQuantityChange(drawerQuantityBtn);
      }
    });
  }

  private attachCartDrawerListeners(): void {
    const closeButton = this.cartDrawer?.querySelector('.sale-cart-drawer__close');
    closeButton?.addEventListener('click', () => this.closeCartDrawer());

    this.cartDrawerBackdrop?.addEventListener('click', () => this.closeCartDrawer());

    document.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key === 'Escape' && this.cartDrawer?.getAttribute('aria-hidden') === 'false') {
        this.closeCartDrawer();
      }
    });
  }

  private initializeTagFilters(): void {
    const t2Container = document.querySelector('.sale-tag-filters--tier2') as HTMLElement;
    if (t2Container) {
      t2Container.hidden = true;
    }
    
    const allT2Buttons = document.querySelectorAll('.sale-tag-filter--tier2');
    allT2Buttons.forEach(btn => btn.classList.remove('active'));
    
    const t1Buttons = document.querySelectorAll('.sale-tag-filter--tier1');
    t1Buttons.forEach(btn => {
      const t1Tag = btn.getAttribute('data-tag');
      const visibleT2Tags = btn.getAttribute('data-visible-t2-tags');
      
      if (t1Tag && visibleT2Tags) {
        const t2Set = new Set(
          visibleT2Tags.split(',').map(tag => tag.trim()).filter(tag => tag.length > 0)
        );
        if (t2Set.size > 0) {
          this.t1ToT2Map.set(t1Tag, t2Set);
        }
      }
    });
  }

  private updateT2Visibility(): void {
    const allowedT2Tags = new Set<string>();
    
    this.selectedT1Tags.forEach(t1Tag => {
      const t2Set = this.t1ToT2Map.get(t1Tag);
      if (t2Set) {
        t2Set.forEach(t2Tag => allowedT2Tags.add(t2Tag));
      } else {
        allowedT2Tags.clear();
        return;
      }
    });
    
    const showAllT2 = allowedT2Tags.size === 0;
    
    const allT2Buttons = document.querySelectorAll('.sale-tag-filter--tier2');
    allT2Buttons.forEach(btn => {
      const t2Tag = btn.getAttribute('data-tag');
      if (!t2Tag) return;
      
      if (showAllT2 || allowedT2Tags.has(t2Tag)) {
        btn.classList.remove('is-hidden');
      } else {
        btn.classList.add('is-hidden');
        
        if (btn.classList.contains('active')) {
          btn.classList.remove('active');
          this.selectedT2Tags.delete(t2Tag);
          this.selectedTags.delete(t2Tag);
        }
      }
    });
  }

  private initializeQuantityButtonStates(): void {
    const productCards = document.querySelectorAll('.sale-product-card');
    productCards.forEach(card => {
      this.updateQuantityButtonStates(card as HTMLElement);
    });
  }

  private handleTagFilter(button: HTMLElement): void {
    const selectedTag = button.dataset.tag;
    if (!selectedTag) return;

    if (selectedTag === 'all') {
      this.selectedTags.clear();
      this.selectedT1Tags.clear();
      this.selectedT2Tags.clear();
      const allFilterButtons = document.querySelectorAll('.sale-tag-filter');
      allFilterButtons.forEach(btn => btn.classList.remove('active'));
      button.classList.add('active');
      
      const t2Container = document.querySelector('.sale-tag-filters--tier2') as HTMLElement;
      if (t2Container) {
        t2Container.hidden = true;
      }
      
      const allT2Buttons = document.querySelectorAll('.sale-tag-filter--tier2');
      allT2Buttons.forEach(btn => btn.classList.remove('is-hidden'));
      
      const productCards = document.querySelectorAll('.sale-collection-grid .sale-product-card');
      productCards.forEach(card => card.classList.remove('hidden'));
      this.updateSectionTitle(null);
      this.hideEmptyState();
      this.scrollTitleToTop();
      return;
    }

    const allButton = document.querySelector('.sale-tag-filter[data-tag="all"]');
    if (allButton) {
      allButton.classList.remove('active');
    }

    const tier = button.dataset.tier || '1';

    if (tier === '1') {
      const isAlreadyActive = button.classList.contains('active');
      
      if (isAlreadyActive) {
        this.selectedT1Tags.clear();
        this.selectedT2Tags.clear();
        this.selectedTags.clear();
        
        const allT1Buttons = document.querySelectorAll('.sale-tag-filter--tier1');
        allT1Buttons.forEach(btn => btn.classList.remove('active'));
        
        const allT2Buttons = document.querySelectorAll('.sale-tag-filter--tier2');
        allT2Buttons.forEach(btn => {
          btn.classList.remove('active');
          btn.classList.remove('is-hidden');
        });
        
        const t2Container = document.querySelector('.sale-tag-filters--tier2') as HTMLElement;
        if (t2Container) {
          t2Container.hidden = true;
        }
        
        const productCards = document.querySelectorAll('.sale-collection-grid .sale-product-card');
        productCards.forEach(card => card.classList.remove('hidden'));
        this.updateSectionTitle(null);
        this.hideEmptyState();
        if (allButton) {
          allButton.classList.add('active');
        }
        this.scrollTitleToTop();
        return;
      }
      
      const allT1Buttons = document.querySelectorAll('.sale-tag-filter--tier1');
      allT1Buttons.forEach(btn => btn.classList.remove('active'));
      this.selectedT1Tags.clear();
      
      button.classList.add('active');
      this.selectedT1Tags.add(selectedTag);
      this.selectedTags.add(selectedTag);
      
      const allT2Buttons = document.querySelectorAll('.sale-tag-filter--tier2');
      allT2Buttons.forEach(btn => {
        btn.classList.remove('active');
        const t2Tag = btn.getAttribute('data-tag');
        if (t2Tag) {
          this.selectedTags.delete(t2Tag);
        }
      });
      this.selectedT2Tags.clear();
      
      const t2Container = document.querySelector('.sale-tag-filters--tier2') as HTMLElement;
      if (t2Container) {
        t2Container.hidden = false;
        this.updateT2Visibility();
      }
      
      this.applyFiltering();
      
    } else if (tier === '2') {
      if (this.selectedT1Tags.size === 0) {
        return;
      }
      
      const isAlreadyActive = button.classList.contains('active');
      
      if (isAlreadyActive) {
        button.classList.remove('active');
        this.selectedT2Tags.clear();
        this.selectedTags.delete(selectedTag);
        
        this.applyFiltering();
        return;
      }
      
      const allT2Buttons = document.querySelectorAll('.sale-tag-filter--tier2');
      allT2Buttons.forEach(btn => btn.classList.remove('active'));
      this.selectedT2Tags.clear();
      
      button.classList.add('active');
      this.selectedT2Tags.add(selectedTag);
      this.selectedTags.add(selectedTag);
      
      this.applyFiltering();
    }
  }

  private applyFiltering(): void {
    const productCards = document.querySelectorAll('.sale-collection-grid .sale-product-card');
    let visibleCount = 0;

    if (this.selectedT1Tags.size === 0 && this.selectedT2Tags.size === 0) {
      productCards.forEach(card => card.classList.remove('hidden'));
      this.updateSectionTitle(null);
      this.hideEmptyState();
      const allButton = document.querySelector('.sale-tag-filter[data-tag="all"]');
      if (allButton) {
        allButton.classList.add('active');
      }
      this.scrollTitleToTop();
      return;
    }

    const t1Tag = this.selectedT1Tags.size === 1 ? Array.from(this.selectedT1Tags)[0] : null;
    const t2Tag = this.selectedT2Tags.size === 1 ? Array.from(this.selectedT2Tags)[0] : null;

    productCards.forEach(card => {
      const cardElement = card as HTMLElement;
      const productTags = cardElement.dataset.tags || '';
      const tagsArray = productTags.split(',').map(tag => tag.trim());
      
      let matches = false;
      
      if (t1Tag && t2Tag) {
        matches = tagsArray.includes(t1Tag) && tagsArray.includes(t2Tag);
      } else if (t1Tag) {
        matches = tagsArray.includes(t1Tag);
      }
      
      if (matches) {
        cardElement.classList.remove('hidden');
        visibleCount++;
      } else {
        cardElement.classList.add('hidden');
      }
    });

    if (visibleCount === 0) {
      this.showEmptyState();
    } else {
      this.hideEmptyState();
    }

    let titleText = '';
    if (t1Tag && t2Tag) {
      titleText = `${t1Tag} × ${t2Tag}`;
    } else if (t1Tag) {
      titleText = t1Tag;
    }
    
    this.updateSectionTitle(titleText || null);
    this.scrollTitleToTop();
  }

  private showEmptyState(): void {
    let emptyState = document.querySelector('.sale-empty-state') as HTMLElement;
    
    if (!emptyState) {
      emptyState = document.createElement('div');
      emptyState.className = 'sale-empty-state';
      emptyState.innerHTML = `
        <div class="sale-empty-state__content">
          <p class="sale-empty-state__message">選択した条件に該当する商品が見つかりませんでした</p>
          <button type="button" class="sale-tag-filter sale-empty-state__reset" data-tag="all">
            条件をリセットする
          </button>
        </div>
      `;
      
      const grid = document.querySelector('.sale-collection-grid__grid');
      if (grid) {
        grid.appendChild(emptyState);
      }
    }
    
    emptyState.style.display = 'block';
  }

  private hideEmptyState(): void {
    const emptyState = document.querySelector('.sale-empty-state') as HTMLElement;
    if (emptyState) {
      emptyState.style.display = 'none';
    }
  }

  private scrollTitleToTop(): void {
    const titleElement = document.querySelector('.sale-collection-grid__title') as HTMLElement;
    if (!titleElement) return;

    const header = document.querySelector('#shopify-section-header, header[role="banner"], .header-wrapper') as HTMLElement | null;
    const offset = header?.offsetHeight || 0;
    
    const titleRect = titleElement.getBoundingClientRect();
    const scrollY = window.pageYOffset || document.documentElement.scrollTop;
    const targetY = titleRect.top + scrollY - offset - 8;

    window.scrollTo({
      top: targetY,
      behavior: 'smooth'
    });
  }

  private updateSectionTitle(tagName: string | null): void {
    const titleElement = document.querySelector('.sale-collection-grid__title') as HTMLElement;
    if (!titleElement) return;

    const defaultTitle = titleElement.dataset.defaultTitle || 'すべての商品';
    
    if (tagName) {
      titleElement.textContent = tagName;
    } else {
      titleElement.innerHTML = defaultTitle;
    }
  }

  private handleQuantityChange(button: HTMLElement): void {
    const productCard = button.closest('.sale-product-card') as HTMLElement;
    if (!productCard) return;

    const input = productCard.querySelector('.sale-quantity__input') as HTMLInputElement;
    if (!input) return;

    const action = button.dataset.action;
    let currentValue = parseInt(input.value) || 1;

    if (action === 'increase') {
      currentValue++;
    } else if (action === 'decrease' && currentValue > 1) {
      currentValue--;
    }

    input.value = currentValue.toString();
    
    this.updateQuantityButtonStates(productCard);
  }

  private updateQuantityButtonStates(productCard: HTMLElement): void {
    const input = productCard.querySelector('.sale-quantity__input') as HTMLInputElement;
    const decreaseBtn = productCard.querySelector('.sale-quantity__btn[data-action="decrease"]') as HTMLButtonElement;
    
    if (!input || !decreaseBtn) return;
    
    const currentValue = parseInt(input.value) || 1;
    
    if (currentValue <= 1) {
      decreaseBtn.disabled = true;
      decreaseBtn.style.opacity = '0.5';
      decreaseBtn.style.cursor = 'not-allowed';
    } else {
      decreaseBtn.disabled = false;
      decreaseBtn.style.opacity = '';
      decreaseBtn.style.cursor = '';
    }
  }

  private handleVariantChange(button: HTMLElement): void {
    const productCard = button.closest('.sale-product-card') as HTMLElement;
    if (!productCard) return;

    const allOptions = productCard.querySelectorAll('.sale-variant-option');
    allOptions.forEach(option => option.classList.remove('selected'));

    button.classList.add('selected');

    const variantId = button.dataset.variantId;
    const addToCartBtn = productCard.querySelector('.sale-add-to-cart') as HTMLElement;
    if (addToCartBtn && variantId) {
      addToCartBtn.dataset.variantId = variantId;
    }

    // Price display is intentionally NOT updated when variant changes
    // to maintain the metafield or product.price value

    const available = button.dataset.available === 'true';
    if (addToCartBtn instanceof HTMLButtonElement) {
      addToCartBtn.disabled = !available;
      if (!available) {
        addToCartBtn.textContent = '完売しました';
        addToCartBtn.classList.add('sold-out');
      } else {
        addToCartBtn.textContent = 'カートに追加する';
        addToCartBtn.classList.remove('sold-out');
      }
    }
  }

  private async handleAddToCart(button: HTMLElement): Promise<void> {
    const productCard = button.closest('.sale-product-card') as HTMLElement;
    if (!productCard) return;

    const variantId = button.dataset.variantId;
    const quantityInput = productCard.querySelector('.sale-quantity__input') as HTMLInputElement;
    const quantity = parseInt(quantityInput?.value || '1');

    if (!variantId) {
      this.showToast('商品を選択してください', 'error');
      return;
    }

    if (button instanceof HTMLButtonElement) {
      button.disabled = true;
    }
    button.classList.add('loading');

    try {
      const properties: Record<string, string> = {};
      
      if (button.dataset.variantPrice) {
        properties._variant_price_cents = button.dataset.variantPrice;
      }
      if (button.dataset.variantComparePrice) {
        properties._variant_compare_price_cents = button.dataset.variantComparePrice;
      }
      if (button.dataset.metafieldPrice) {
        properties._metafield_price_cents = button.dataset.metafieldPrice;
      }
      if (button.dataset.metafieldComparePrice) {
        properties._metafield_compare_price_cents = button.dataset.metafieldComparePrice;
      }
      if (button.dataset.title) {
        properties._display_title = button.dataset.title;
      }
      if (button.dataset.freeShippingFlag === 'true') {
        properties._free_shipping_flag = 'true';
      }

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: variantId,
          quantity: quantity,
          properties: Object.keys(properties).length > 0 ? properties : undefined,
        }),
      });

      if (!response.ok) {
        throw new Error('カートへの追加に失敗しました');
      }

      const data: AddToCartResponse = await response.json();

      this.showToast('カートに追加しました', 'success');

      if (quantityInput) {
        quantityInput.value = '1';
      }

      await this.updateCartCount();
      await this.updateStickyCheckout();

      this.publishCartUpdate(data);

      if (!this.hasOpenedDrawer) {
        setTimeout(() => {
          this.openCartDrawer();
          this.hasOpenedDrawer = true;
        }, 500);
      }

    } catch (error) {
      console.error('Error adding to cart:', error);
      this.showToast((error as Error).message || 'エラーが発生しました', 'error');
    } finally {
      if (button instanceof HTMLButtonElement) {
        button.disabled = false;
      }
      button.classList.remove('loading');
    }
  }

  private async updateCartCount(): Promise<void> {
    try {
      const response = await fetch('/cart.js');
      const cart: CartResponse = await response.json();

      const cartCountElements = document.querySelectorAll('.cart-count-bubble span');
      cartCountElements.forEach((element) => {
        element.textContent = cart.item_count.toString();
        
        const bubble = element.closest('.cart-count-bubble') as HTMLElement;
        if (bubble) {
          if (cart.item_count > 0) {
            bubble.style.display = '';
          } else {
            bubble.style.display = 'none';
          }
        }
      });
    } catch (error) {
      console.error('Error updating cart count:', error);
    }
  }

  private formatPrice(cents: number): string {
    return '¥' + (cents / 100).toLocaleString('ja-JP');
  }

  /**
   * Compute sale and compare prices from all 4 sources (variant + metafield prices)
   * Returns min as sale price, max as compare price
   */
  private computeItemPrices(item: CartResponse['items'][0]): { saleCents: number; compareCents: number } {
    const props = item.properties || {};
    
    const sources: number[] = [];
    
    if (props._variant_price_cents) {
      const val = typeof props._variant_price_cents === 'string' 
        ? parseInt(props._variant_price_cents, 10) 
        : props._variant_price_cents;
      if (Number.isFinite(val) && val > 0) sources.push(val);
    }
    
    if (props._variant_compare_price_cents) {
      const val = typeof props._variant_compare_price_cents === 'string'
        ? parseInt(props._variant_compare_price_cents, 10)
        : props._variant_compare_price_cents;
      if (Number.isFinite(val) && val > 0) sources.push(val);
    }
    
    if (props._metafield_price_cents) {
      const val = typeof props._metafield_price_cents === 'string'
        ? parseInt(props._metafield_price_cents, 10)
        : props._metafield_price_cents;
      if (Number.isFinite(val) && val > 0) sources.push(val);
    }
    
    if (props._metafield_compare_price_cents) {
      const val = typeof props._metafield_compare_price_cents === 'string'
        ? parseInt(props._metafield_compare_price_cents, 10)
        : props._metafield_compare_price_cents;
      if (Number.isFinite(val) && val > 0) sources.push(val);
    }
    
    if (sources.length === 0) {
      if (item.price > 0) sources.push(item.price);
      if (item.original_price && item.original_price > 0) sources.push(item.original_price);
      if (item.final_price && item.final_price > 0) sources.push(item.final_price);
    }
    
    const saleCents = sources.length > 0 ? Math.min(...sources) : (item.price || 0);
    const compareCents = sources.length > 0 ? Math.max(...sources) : (item.original_price || item.price || 0);
    
    return { saleCents, compareCents };
  }

  private async updateStickyCheckout(): Promise<void> {
    if (!this.stickyCheckout) return;

    try {
      const response = await fetch('/cart.js');
      const cart: CartResponse = await response.json();

      const countElement = this.stickyCheckout.querySelector('.sale-sticky-checkout__count') as HTMLElement;
      const originalPriceElement = this.stickyCheckout.querySelector('.sale-sticky-checkout__price-original') as HTMLElement;
      const discountPriceElement = this.stickyCheckout.querySelector('.sale-sticky-checkout__price-discount') as HTMLElement;

      if (countElement) {
        countElement.textContent = cart.item_count.toString();
      }

      let regularTotal = 0;
      let discountedTotal = 0;
      let freeShippingTotal = 0;
      let hasFreeShippingFlagItem = false;

      cart.items.forEach(item => {
        const qty = item.quantity;
        
        const { saleCents, compareCents } = this.computeItemPrices(item);
        regularTotal += qty * compareCents;
        discountedTotal += qty * saleCents;
        
        const sku = item.sku || '';
        if (!sku.includes('T') && sku.includes('Z')) {
          freeShippingTotal += qty * saleCents;
        }

        // Check for free shipping flag in line item properties
        const props = item.properties || {};
        if (props._free_shipping_flag === 'true') {
          hasFreeShippingFlagItem = true;
        }
      });

            const arrowElement = this.stickyCheckout.querySelector('.sale-sticky-checkout__arrow') as HTMLElement;
            const isSamePrice = regularTotal === discountedTotal;

            if (originalPriceElement) {
              originalPriceElement.textContent = isSamePrice ? '' : this.formatPrice(regularTotal);
              originalPriceElement.style.display = isSamePrice ? 'none' : '';
            }
            if (arrowElement) {
              arrowElement.style.display = isSamePrice ? 'none' : '';
            }
            if (discountPriceElement) {
              discountPriceElement.textContent = this.formatPrice(discountedTotal);
            }

            this.updateShippingBadge(freeShippingTotal, hasFreeShippingFlagItem);

      if (cart.item_count > 0) {
        this.stickyCheckout.classList.add('visible');
      } else {
        this.stickyCheckout.classList.remove('visible');
      }
    } catch (error) {
      console.error('Error updating sticky checkout:', error);
    }
  }

  private updateShippingBadge(discountedTotal: number, hasFreeShippingFlagItem: boolean = false): void {
    const badge = this.stickyCheckout?.querySelector('.sale-shipping-badge') as HTMLElement;
    if (!badge) return;

    const progressEl = badge.querySelector('.sale-shipping-badge__progress') as HTMLElement;
    const textEl = badge.querySelector('.sale-shipping-badge__text') as HTMLElement;
    const srEl = badge.querySelector('.sale-shipping-badge__sr') as HTMLElement;

    // If any item has free shipping flag, show free shipping immediately
    if (hasFreeShippingFlagItem) {
      badge.style.display = '';
      badge.classList.add('achieved');
      if (progressEl) {
        progressEl.style.setProperty('--progress-angle', '360deg');
      }
      if (textEl) textEl.textContent = '送料無料で\nお届け';
      if (srEl) srEl.textContent = '送料無料でお届けします';

      if (!this.freeShipAchieved) {
        this.triggerConfetti();
        this.freeShipAchieved = true;
      }
      return;
    }

    const progress = Math.min(discountedTotal / this.FREE_SHIPPING_THRESHOLD, 1);
    const angle = Math.round(progress * 360);

    if (progressEl) {
      progressEl.style.setProperty('--progress-angle', `${angle}deg`);
    }

    if (discountedTotal >= this.DISPLAY_THRESHOLD) {
      badge.style.display = '';
    } else {
      badge.style.display = 'none';
      return;
    }

    if (discountedTotal >= this.FREE_SHIPPING_THRESHOLD) {
      badge.classList.add('achieved');
      if (progressEl) {
        progressEl.style.setProperty('--progress-angle', '360deg');
      }
      if (textEl) textEl.textContent = '送料無料で\nお届け';
      if (srEl) srEl.textContent = '送料無料でお届けします';

      if (!this.freeShipAchieved) {
        this.triggerConfetti();
        this.freeShipAchieved = true;
      }
    } else {
      badge.classList.remove('achieved');
      const remainingCents = Math.max(0, this.FREE_SHIPPING_THRESHOLD - discountedTotal);
      const remainingYen = Math.ceil(remainingCents / 100);
      
      if (textEl) textEl.textContent = `送料無料まで\nあと¥${remainingYen.toLocaleString('ja-JP')}`;
      if (srEl) srEl.textContent = `送料無料まであと${remainingYen.toLocaleString('ja-JP')}円`;

      if (this.freeShipAchieved) {
        this.freeShipAchieved = false;
      }
    }
  }

  private triggerConfetti(): void {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) return;

    const win = window as any;
    if (typeof win.confetti === 'function') {
      win.confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.8, x: 0.1 },
        colors: ['#D4AF37', '#FFD700', '#FFA500'],
        disableForReducedMotion: true
      });
    }
  }

  private publishCartUpdate(data: AddToCartResponse): void {
    const win = window as any;
    if (typeof win.PUB_SUB_EVENTS !== 'undefined' && typeof win.publish === 'function') {
      win.publish(win.PUB_SUB_EVENTS.cartUpdate, {
        source: 'sale-cart',
        cartData: data,
        variantId: data.variant_id,
      });
    }
  }

  private showToast(message: string, type: 'success' | 'error' = 'success'): void {
    const existingToast = document.querySelector('.sale-toast');
    if (existingToast) {
      existingToast.remove();
    }

    const toast = document.createElement('div');
    toast.className = `sale-toast ${type}`;
    toast.textContent = message;
    document.body.appendChild(toast);

    setTimeout(() => {
      toast.style.animation = 'saleSlideIn 0.3s ease reverse';
      setTimeout(() => {
        toast.remove();
      }, 300);
    }, 800);
  }

  private async openCartDrawer(): Promise<void> {
    if (!this.cartDrawer) return;

    this.scrollLockY = window.scrollY || window.pageYOffset;

    await this.renderCartItems();

    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this.scrollLockY}px`;
    document.body.style.width = '100%';
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }
    document.body.classList.add('sale-cart-drawer-open');

    this.cartDrawer.setAttribute('aria-hidden', 'false');
  }

  private closeCartDrawer(): void {
    if (!this.cartDrawer) return;

    this.cartDrawer.setAttribute('aria-hidden', 'true');

    setTimeout(() => {
      document.body.classList.remove('sale-cart-drawer-open');
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.paddingRight = '';
      window.scrollTo(0, this.scrollLockY);
    }, 300);
  }

  private async renderCartItems(): Promise<void> {
    if (!this.cartDrawerItems || !this.cartDrawerEmpty) return;

    try {
      const response = await fetch('/cart.js');
      const cart: CartResponse = await response.json();

      if (cart.item_count === 0) {
        this.cartDrawerEmpty.style.display = 'block';
        this.cartDrawerItems.style.display = 'none';
        this.updateCartDrawerFooter(0, 0);
        return;
      }

      this.cartDrawerEmpty.style.display = 'none';
      this.cartDrawerItems.style.display = 'block';

      let regularTotal = 0;
      let discountedTotal = 0;

      this.cartDrawerItems.innerHTML = '';

      cart.items.forEach((item, index) => {
        const { saleCents, compareCents } = this.computeItemPrices(item);

        regularTotal += item.quantity * compareCents;
        discountedTotal += item.quantity * saleCents;

        const itemEl = document.createElement('div');
        itemEl.className = 'sale-cart-item';
        itemEl.dataset.lineIndex = (index + 1).toString();

                const displayTitle = item.properties?._display_title || item.product_title || item.title || '';
                const variantTitle = item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : '';
                const imageUrl = item.image || '';
                const isItemSamePrice = saleCents === compareCents;

                itemEl.innerHTML = `
                  <div class="sale-cart-item__image">
                    ${imageUrl ? `<img src="${imageUrl}" alt="${displayTitle}" loading="lazy">` : ''}
                  </div>
                  <div class="sale-cart-item__details">
                    <h3 class="sale-cart-item__title">${displayTitle}</h3>
                    ${variantTitle ? `<p class="sale-cart-item__variant">${variantTitle}</p>` : ''}
                    <div class="sale-cart-item__prices">
                      ${isItemSamePrice ? '' : `<span class="sale-cart-item__price-original">${this.formatPrice(compareCents)}</span>`}
                      ${isItemSamePrice ? '' : `<span class="sale-cart-item__arrow">→</span>`}
                      <span class="sale-cart-item__price-discount">${this.formatPrice(saleCents)}</span>
                    </div>
                  </div>
          <div class="sale-cart-item__actions">
            <div class="sale-cart-item__quantity">
              <button type="button" class="sale-cart-item__quantity-btn" data-action="decrease" aria-label="数量を減らす">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M3 8H13" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                </svg>
              </button>
              <span class="sale-cart-item__quantity-value">${item.quantity}</span>
              <button type="button" class="sale-cart-item__quantity-btn" data-action="increase" aria-label="数量を増やす">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M8 3V13M3 8H13" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                </svg>
              </button>
            </div>
          </div>
        `;

        if (this.cartDrawerItems) {
          this.cartDrawerItems.appendChild(itemEl);
        }
      });

      this.updateCartDrawerFooter(regularTotal, discountedTotal);
    } catch (error) {
      console.error('Error rendering cart items:', error);
    }
  }

    private updateCartDrawerFooter(regularTotal: number, discountedTotal: number): void {
      const originalEl = this.cartDrawer?.querySelector('.sale-cart-drawer__total-original') as HTMLElement;
      const arrowEl = this.cartDrawer?.querySelector('.sale-cart-drawer__total-arrow') as HTMLElement;
      const discountEl = this.cartDrawer?.querySelector('.sale-cart-drawer__total-discount') as HTMLElement;
      const isSamePrice = regularTotal === discountedTotal;

      if (originalEl) {
        originalEl.textContent = isSamePrice ? '' : this.formatPrice(regularTotal);
        originalEl.style.display = isSamePrice ? 'none' : '';
      }
      if (arrowEl) {
        arrowEl.style.display = isSamePrice ? 'none' : '';
      }
      if (discountEl) {
        discountEl.textContent = this.formatPrice(discountedTotal);
      }
    }

  private async handleDrawerQuantityChange(button: HTMLElement): Promise<void> {
    if (this.isUpdatingCart) return;

    const action = button.dataset.action;
    const itemEl = button.closest('.sale-cart-item') as HTMLElement;
    if (!itemEl) return;

    const lineIndex = parseInt(itemEl.dataset.lineIndex || '0');
    if (!lineIndex) return;

    const quantityEl = itemEl.querySelector('.sale-cart-item__quantity-value') as HTMLElement;
    const currentQuantity = parseInt(quantityEl?.textContent || '0');

    let newQuantity = currentQuantity;
    if (action === 'increase') {
      newQuantity = currentQuantity + 1;
    } else if (action === 'decrease') {
      newQuantity = Math.max(0, currentQuantity - 1);
    }

    if (newQuantity === currentQuantity) return;

    this.isUpdatingCart = true;

    const buttons = itemEl.querySelectorAll('.sale-cart-item__quantity-btn');
    buttons.forEach(btn => (btn as HTMLButtonElement).disabled = true);

    if (this.cartDrawerPanel) {
      this.cartDrawerPanel.classList.add('loading');
    }

    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          line: lineIndex,
          quantity: newQuantity,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to update cart');
      }

      await this.renderCartItems();
      await this.updateStickyCheckout();
    } catch (error) {
      console.error('Error updating cart quantity:', error);
      this.showToast('カートの更新に失敗しました', 'error');
      buttons.forEach(btn => (btn as HTMLButtonElement).disabled = false);
    } finally {
      this.isUpdatingCart = false;
      if (this.cartDrawerPanel) {
        this.cartDrawerPanel.classList.remove('loading');
      }
    }
  }

}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new SaleCart();
    new ProductModal();
  });
} else {
  new SaleCart();
  new ProductModal();
}

export {};
